import { useState } from 'react';
import type { Product, ProductKind } from '@salonomy/types';
import {
  Button,
  Card,
  EmptyState,
  Field,
  Pill,
  Select,
  Spinner,
  TextInput,
  Toggle,
} from '@salonomy/ui';
import { ConfirmModal } from '../../components/ConfirmModal.js';
import { useDeactivateProduct, useProducts, useSuppliers } from '../../data/stock.js';
import { money } from '../../lib/format.js';
import { useToast } from '../../lib/toast.js';
import { ProductModal } from './ProductModal.js';
import './stock.css';

const KIND_FILTERS: Array<{ value: '' | ProductKind; label: string }> = [
  { value: '', label: 'All kinds' },
  { value: 'retail', label: 'Retail' },
  { value: 'professional', label: 'Professional' },
];

const KIND_LABEL: Record<ProductKind, string> = { retail: 'Retail', professional: 'Professional' };

/**
 * The product catalogue — a paged, filterable table off `useProducts`. Each row shows the SERVER's
 * computed `onHand` and the per-unit cost/price; on-hand is never recomputed here. A row can be edited
 * (attributes only) or archived (soft delete = active:false). The `active` filter defaults to active-
 * only but can include archived rows so a deactivated product stays inspectable.
 */
export function ProductsPanel() {
  const toast = useToast();
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<'' | ProductKind>('');
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [offset, setOffset] = useState(0);
  const [modal, setModal] = useState<{ existing?: Product } | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<Product | null>(null);
  const [busy, setBusy] = useState(false);

  const { data: suppliers } = useSuppliers();
  const deactivate = useDeactivateProduct();

  // `active: true` = active-only; leaving it undefined returns active+inactive. lowStockOnly/kind/q only
  // when set — the server omits an undefined filter.
  const { data, isLoading, isError, isFetching } = useProducts(
    {
      q: q.trim() || undefined,
      kind: kind || undefined,
      lowStockOnly: lowStockOnly || undefined,
      active: includeInactive ? undefined : true,
    },
    offset,
  );

  const rows = data?.data ?? [];
  const total = data?.pagination.total ?? 0;
  const limit = data?.pagination.limit ?? 50;

  function resetPage<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setOffset(0);
    };
  }

  async function confirmArchive() {
    if (!archiveTarget) return;
    setBusy(true);
    try {
      await deactivate.mutateAsync(archiveTarget.id);
      toast.success(`${archiveTarget.name} archived`);
      setArchiveTarget(null);
    } catch {
      toast.error('Could not archive this product.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <div className="stock-head">
        <div>
          <div className="section-title" style={{ marginBottom: 2 }}>
            Products
          </div>
          <div className="muted" style={{ fontSize: 13 }}>
            Your retail and professional catalogue. On-hand is the running sum of stock movements.
          </div>
        </div>
        <Button size="sm" onClick={() => setModal({})}>
          Add product
        </Button>
      </div>

      <div className="stock-filters">
        <div className="stock-filters__grow">
          <Field label="Search">
            <TextInput
              value={q}
              onChange={(e) => resetPage(setQ)(e.target.value)}
              placeholder="Name, SKU or barcode"
            />
          </Field>
        </div>
        <div style={{ minWidth: 160 }}>
          <Field label="Kind">
            <Select
              value={kind}
              onChange={(e) => resetPage(setKind)(e.target.value as '' | ProductKind)}
            >
              {KIND_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </div>

      <div className="stock-toggles">
        <Toggle
          checked={lowStockOnly}
          onChange={resetPage(setLowStockOnly)}
          label="Low stock only"
        />
        <Toggle
          checked={includeInactive}
          onChange={resetPage(setIncludeInactive)}
          label="Show archived"
        />
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '16px 0' }}>
          <Spinner size={16} />
          <span className="muted" style={{ fontSize: 13 }}>
            Loading the catalogue…
          </span>
        </div>
      ) : isError ? (
        <div className="muted" style={{ fontSize: 13, padding: '8px 0' }}>
          Couldn&rsquo;t load products. Your stock catalogue may not be set up yet — add a product
          to begin.
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No products yet"
          description="Add your retail and professional stock to track on-hand, value and movements."
          action={
            <Button size="sm" onClick={() => setModal({})}>
              Add product
            </Button>
          }
        />
      ) : (
        <div
          className="stock-table"
          style={{ ['--cols' as string]: 'minmax(0,2fr) 1fr 0.8fr 0.8fr 0.7fr 1.4fr' }}
        >
          <div className="stock-row stock-row--head">
            <span>Product</span>
            <span>Kind</span>
            <span className="stock-num">Cost</span>
            <span className="stock-num">Price</span>
            <span className="stock-num">On-hand</span>
            <span className="stock-actions">Actions</span>
          </div>
          {rows.map((p) => (
            <div key={p.id} className="stock-row">
              <div className="stock-cell-main">
                <span className="stock-cell-main__name">{p.name}</span>
                <span className="muted" style={{ fontSize: 12 }}>
                  {p.sku ? `SKU ${p.sku}` : p.barcode ? `Barcode ${p.barcode}` : '—'}
                  {!p.active ? ' · archived' : ''}
                </span>
              </div>
              <span>{KIND_LABEL[p.kind]}</span>
              <span className="stock-num mono">{money(p.costPence)}</span>
              <span className="stock-num mono">{money(p.pricePence)}</span>
              <span className="stock-num">
                {p.onHand <= p.lowStockThreshold && p.trackStock ? (
                  <Pill tone="danger">{p.onHand}</Pill>
                ) : (
                  <span className="mono">{p.onHand}</span>
                )}
              </span>
              <span className="stock-actions">
                <Button size="sm" variant="ghost" onClick={() => setModal({ existing: p })}>
                  Edit
                </Button>
                {p.active ? (
                  <Button size="sm" variant="ghost" onClick={() => setArchiveTarget(p)}>
                    Archive
                  </Button>
                ) : (
                  <Pill tone="soft">Archived</Pill>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      {total > limit ? (
        <div className="stock-pager">
          <Button
            variant="outline"
            size="sm"
            disabled={offset === 0 || isFetching}
            onClick={() => setOffset((o) => Math.max(0, o - limit))}
          >
            ← Previous
          </Button>
          <span className="muted" style={{ fontSize: 12.5 }}>
            {offset + 1}–{Math.min(offset + limit, total)} of {total}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={offset + limit >= total || isFetching}
            onClick={() => setOffset((o) => o + limit)}
          >
            Next →
          </Button>
        </div>
      ) : null}

      {modal ? (
        <ProductModal
          existing={modal.existing}
          suppliers={suppliers ?? []}
          onClose={() => setModal(null)}
        />
      ) : null}

      <ConfirmModal
        open={Boolean(archiveTarget)}
        title="Archive product"
        confirmLabel="Archive"
        confirmPhrase={archiveTarget?.name}
        busy={busy}
        onConfirm={confirmArchive}
        onClose={() => setArchiveTarget(null)}
      >
        <p style={{ margin: 0 }}>
          Archiving <strong>{archiveTarget?.name}</strong> hides it from the active catalogue and
          the till. Its stock history is preserved and you can reactivate it later by editing it
          (Show archived).
        </p>
      </ConfirmModal>
    </Card>
  );
}
